import ExcelJS from 'exceljs';
import { CargoEventReportData } from '../../ReportGeneratorTypes';
import { setCellValueWithFont, setupWorksheetForPrint, setWorksheetPrintArea } from './reportEventExcelUtils';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import {
    addOdfjellHeader,
    addVesselDetails,
    getCargoOperationForEvent,
    getDecimalValueRegFromEvent,
    getFirstDropdownValueStr,
    getStringValueRegFromEvent,
    getTankListStrFromEvent,
    getWeightStr,
    odfjellHeaderColor,
} from './parts/helpers';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { EventTypeEnum } from 'src/enums/eventTypeEnum';
import { isNullOrUndefined } from 'src/utils/objectUtils';

const addReportHeader = (worksheet: ExcelJS.Worksheet, logoImageId: number): void => {
    let rowNum = addOdfjellHeader(worksheet, logoImageId);
    rowNum = 6;
    setCellValueWithFont(worksheet, rowNum, 1, `CERTIFICATE OF CLEANLINESS`, {
        bold: true,
        color: odfjellHeaderColor,
        fontSize: 16,
        mergeAcross: 9,
    });
};

const getTankInspCommenced = (event: TankersEvent, events: TankersEvent[]) => {
    const evListGroup = events.filter((o) => o.eventTypeGroupSequenceIdentity == event.eventTypeGroupSequenceIdentity);
    const tankInspCommenced = evListGroup.find((o) => o.eventTypeId == EventTypeEnum.TankInspectionCommenced);

    return tankInspCommenced;
};

const addTankDescription = (
    worksheet: ExcelJS.Worksheet,
    selectedEvent: TankersEvent,
    events: TankersEvent[],
    cargoOperation: RotationCargoOperation,
    rowNumber: number
) => {
    rowNumber += 4;
    setCellValueWithFont(
        worksheet,
        rowNumber,
        1,
        `This is to certify that I/we have on ${prettifyDate(selectedEvent.eventDate.actualDateTimeLocal)} inspected the vessel's tanks:`,
        { mergeAcross: 9, align: 'left' }
    );
    rowNumber += 2;
    const tankInspCommenced = getTankInspCommenced(selectedEvent, events);
    setCellValueWithFont(worksheet, rowNumber, 1, getTankListStrFromEvent(tankInspCommenced, 6), {
        mergeAcross: 9,
        mergeDown: 4,
        wrapText: true,
        align: 'left',
    });

    rowNumber += 5;
    setCellValueWithFont(worksheet, rowNumber, 1, `as well as lines and pumps, and found same to be clean, drained, empty and therefore`, {
        mergeAcross: 9,
        wrapText: true,
        align: 'left',
    });

    rowNumber++;
    setCellValueWithFont(
        worksheet,
        rowNumber,
        1,
        `suitable for transport of Cargo ${getWeightStr(cargoOperation, true)} ${cargoOperation.cargo.product.tradeName}.`,
        {
            mergeAcross: 9,
            mergeDown: 2,
            wrapText: true,
            verticalAlign: 'top',
            align: 'left',
        }
    );
    rowNumber += 2;
    return rowNumber;
};

const addPressureInBars = (worksheet: ExcelJS.Worksheet, event: TankersEvent, rowNumber: number): number => {
    const pressure = getDecimalValueRegFromEvent(event, 151);
    if (isNullOrUndefined(pressure)) return rowNumber;

    rowNumber++;
    setCellValueWithFont(
        worksheet,
        rowNumber,
        1,
        `Heating coils are pressure tested at ${pressure} bars by means of ${getFirstDropdownValueStr(event, 52)}.`,
        {
            mergeAcross: 9,
            wrapText: true,
            align: 'left',
        }
    );

    rowNumber++;
    setCellValueWithFont(worksheet, rowNumber, 1, `Heating coils were ${getFirstDropdownValueStr(event, 53)}.`, {
        mergeAcross: 9,
        wrapText: true,
        align: 'left',
    });

    return rowNumber++;
};

const addConnForTankCleaningMachines = (worksheet: ExcelJS.Worksheet, event: TankersEvent, rowNumber: number): number => {
    rowNumber += 2;
    setCellValueWithFont(
        worksheet,
        rowNumber,
        1,
        `Connections for tank cleaning machines - Sealed (Lockout/Tagout): ${getFirstDropdownValueStr(event, 253)}`,
        {
            mergeAcross: 9,
            wrapText: true,
            align: 'left',
        }
    );

    return rowNumber++;
};

const addRemarks = (worksheet: ExcelJS.Worksheet, event: TankersEvent, rowNumber: number): number => {
    rowNumber += 2;
    setCellValueWithFont(worksheet, rowNumber, 1, `Remarks:`, {
        mergeAcross: 9,
        wrapText: true,
        align: 'left',
    });

    rowNumber++;
    const remarks = getStringValueRegFromEvent(event, 98);
    setCellValueWithFont(worksheet, rowNumber, 1, remarks, {
        mergeAcross: 9,
        mergeDown: 6,
        wrapText: true,
        verticalAlign: 'top',
        align: 'left',
    });

    return (rowNumber += 7);
};
const addSignature = (worksheet: ExcelJS.Worksheet, rowNumber: number): number => {
    rowNumber += 3;

    setCellValueWithFont(
        worksheet,
        rowNumber,
        6,
        `Surveyor
(Name, Company and Signature)`,
        {
            mergeAcross: 4,
            mergeDown: 2,
            align: 'center',
            verticalAlign: 'top',
            wrapText: true,
        }
    );

    return rowNumber + 6;
};

const setDefaultColumnWidths = (workheet: ExcelJS.Worksheet) => {
    workheet.getColumn(1).width = 9;
    workheet.getColumn(2).width = 2;
    workheet.getColumn(3).width = 14;
    workheet.getColumn(4).width = 21;
    workheet.getColumn(5).width = 9;
    workheet.getColumn(6).width = 17;
    workheet.getColumn(7).width = 9;
    workheet.getColumn(8).width = 14;
    workheet.getColumn(9).width = 7;
};

export const generateCertificateOfCleanlinessReport = (
    workbook: ExcelJS.Workbook,
    cargoEventReportData: CargoEventReportData,
    logoImageId: number
): void => {
    const _worksheet = workbook.addWorksheet('Certificate of cleanliness');
    setupWorksheetForPrint(_worksheet);
    // Hide gridlines and keep a clean white background
    _worksheet.views = [{ state: 'normal', showGridLines: false }];
    const portCall = cargoEventReportData.selectedPortCall;
    const selectedEvent = cargoEventReportData.tankersEvents.find((ev) => !ev.deleted && ev.eventId === cargoEventReportData.excelReportInfo.eventId);
    const cargoOperation = getCargoOperationForEvent(
        portCall.berthCalls.flatMap((berthCall) => berthCall.cargoOperations),
        selectedEvent
    );
    if (!cargoOperation || !selectedEvent) return;
    setDefaultColumnWidths(_worksheet);
    addReportHeader(_worksheet, logoImageId);
    let rowNumber = addVesselDetails(
        _worksheet,
        8,
        1,
        7,
        portCall.voyageNumber,
        portCall.portDisplayName,
        cargoEventReportData.vessel,
        selectedEvent.cargoNumber,
        selectedEvent.eventDate.actualDateTimeLocal
    );
    rowNumber = addTankDescription(_worksheet, selectedEvent, cargoEventReportData.tankersEvents, cargoOperation, rowNumber);
    rowNumber = addPressureInBars(_worksheet, selectedEvent, rowNumber);
    rowNumber = addConnForTankCleaningMachines(_worksheet, selectedEvent, rowNumber);
    rowNumber = addRemarks(_worksheet, selectedEvent, rowNumber);
    rowNumber = addSignature(_worksheet, rowNumber);

    setWorksheetPrintArea(_worksheet, rowNumber, 9);
};
